import { Editor } from "@monaco-editor/react";
import {
  AddOutlined,
  ArrowBackOutlined,
  Article,
  Functions,
  HelpOutline, // Importado Icone de Ajuda
} from "@mui/icons-material";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import {
  Code,
  AssessmentOutlined,
  DataObject,
  InputOutlined,
  PlayArrowOutlined,
  Terminal,
} from "@mui/icons-material";

import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStyle } from "./styles";
import { notifyOnPage } from "../../../../utils/notifyOnPage";
import api from "../../../../services/api";
import VariaveisForm from "../../../../componentes/easyReport/variaveisForm";
import { PdfPreview } from "../../../../componentes/easyReport/pdfPreview/pdfPreview";
import { evalCode } from "../../../../componentes/easyReport/pdfPreview/utils/evalCode";
import { useRef } from "react";
import { CUSTOM_ITEMS } from "../../../../componentes/easyReport/constants/const";
import { HelpComponent } from "../../newFunction/HelpText"; // Importado component de Ajuda

interface Application {
  name: string;
}

type Applications = Application[];

interface Funcoes {
  id: string;
  titulo: string;
}

export interface Variaveis {
  id?: string;
  title: string;
  nome: string;
  applicationId: string[];
  keyName: { key: string; applicationName: string }[];
  multiple: boolean;
}

interface NewFuncao {
  id?: string;
  nome: string;
  description: string;
  variavel: Variaveis[];
  code: string;
  view: string;
  style: string;
  isDefault?: boolean;
}

function EditFunction() {
  const theme = useTheme();
  const styles = useStyle(theme);
  const navigate = useNavigate();
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [tabsValue, setTabsValue] = useState(0);

  const [newFuncao, setNewFuncao] = useState<NewFuncao>({
    nome: "",
    description: "",
    variavel: [
      {
        title: "",
        nome: "",
        applicationId: [""],
        keyName: [],
        multiple: false,
      },
    ],
    code: "",
    view: "",
    style: "",
  });
  const [allFuncoes, setAllFuncoes] = useState<Funcoes[]>([]);
  const [allApplications, setApplications] = useState<Applications>([]);
  const [itemsOptions, setItemsOptions] = useState<
    { name: string; key_: string; applicationName: string }[][]
  >([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [logsContent, setLogsContent] = useState<any>("");
  const [pdfError, setPdfError] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { id } = useParams();

  // Add refs for debouncing
  const codeDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const viewStyleDebounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setPageLoading(true);
        const [data, functions, res] = await Promise.all([
          api.post("/easyReport/v2/findFunction", { id: id }),
          api.get("/easyReport/v2/findManyFunctions"),
          api.get("easyReport/v2/findManyApplications"),
        ]);
        setNewFuncao(data.data);
        setAllFuncoes(functions.data.filter((item: Funcoes) => item.id !== id));
        setApplications(res.data as Applications);

        // Coleta itens
        if (data.data.variavel && data.data.variavel.length > 0) {
          const results = await Promise.all(
            data.data.variavel.map(async (v: Variaveis) => {
              if (v.applicationId && v.applicationId.length > 0) {
                if (v.applicationId.includes("CUSTOM")) {
                  return CUSTOM_ITEMS;
                }
                try {
                  const res = await api.post(
                    `/easyReport/v2/findManyItemsName`,
                    {
                      applicationName: v.applicationId,
                    },
                  );
                  return res.data || [];
                } catch {
                  return [];
                }
              }
              return [];
            }),
          );
          setItemsOptions(results);
        }
      } catch (error: any) {
        notifyOnPage(
          "easyReportV2/perfilRelatorios/novaFuncao",
          error?.response?.data?.error || "Erro carregar aplicação",
          "error",
          3000,
        );
      } finally {
        setPageLoading(false);
      }
    };
    fetchData();
  }, []);

  const allApplicationsMemo = useMemo(() => allApplications, [allApplications]);

  const isLarge = useMediaQuery(theme.breakpoints.down("lg"));
  function isFieldDuplicated(
    variavel: Variaveis[],
    value: string,
    field: keyof Pick<Variaveis, "nome" | "title">,
  ): boolean {
    const normalized = value?.trim().toLowerCase();
    if (!normalized) return false;
    return (
      variavel.filter(
        (v) => (v[field] || "")?.trim().toLowerCase() === normalized,
      ).length > 1
    );
  }

  const handleSave = async () => {
    try {
      const response = await api.put("/easyReport/v2/editFunction", newFuncao);
      if (response && response.status === 200) {
        notifyOnPage(
          "easyReportV2/perfilRelatorios",
          "Função criada com sucesso",
          "success",
          3000,
        );
        navigate("/easyReportV2/perfilRelatorios/funcao");
      }
    } catch (error: any) {
      notifyOnPage(
        "easyReportV2/perfilRelatorios/funcao",
        error?.response?.data?.error || "Erro ao atualizar função",
        "error",
        3000,
      );
    }
  };

  const handleApplicationChange = async (
    index: number,
    v: Variaveis,
    newValue: Application[] | null,
  ) => {
    const appNames = newValue?.map((app) => app.name);
    setNewFuncao((prev) => ({
      ...prev,
      variavel: prev.variavel.map((item, i) =>
        i === index
          ? {
              ...item,
              applicationId: Array.isArray(appNames) ? appNames : [""],
              keyName: [],
              multiple: item.multiple ?? false,
              title: item.title,
              nome: item.nome,
              id: item.id,
            }
          : item,
      ),
    }));
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    const signal = controller.signal;

    abortControllerRef.current = controller;
    if (Array.isArray(appNames) && appNames.length > 0) {
      try {
        if (appNames.includes("CUSTOM")) {
          const idx = newFuncao.variavel.findIndex((item) => item === v);
          setItemsOptions((prev) => {
            const updated = [...prev];
            updated[idx] = CUSTOM_ITEMS;
            return updated;
          });
        } else {
          setItemsLoading(true);
          const res = await api.post(
            `/easyReport/v2/findManyItemsName`,
            {
              applicationName: appNames,
            },
            { signal },
          );
          const idx = newFuncao.variavel.findIndex((item) => item === v);
          setItemsOptions((prev) => {
            const updated = [...prev];
            updated[idx] = res.data || [];
            return updated;
          });
        }
      } catch (error: any) {
        notifyOnPage(
          "easyReportV2/perfilRelatorios/novaFuncao",
          error?.response?.data?.error || "Erro ao carregar itens",
          "error",
          3000,
        );
      } finally {
        setItemsLoading(false);
      }
    }
  };

  const handleDelete = (index: number) => {
    setNewFuncao((prev) => ({
      ...prev,
      variavel: prev.variavel.filter((_, i) => i !== index),
    }));
  };

  const handleClickRun = () => {
    const evaluatedCode = evalCode(
      newFuncao.code,
      variablesMemo[0],
      setLogsContent,
    );
    if (evaluatedCode) {
      setLogsContent(evaluatedCode);
    }
  };

  const isNomeDuplicated = useMemo(
    () =>
      allFuncoes.some(
        (p) =>
          newFuncao.nome.trim().toLowerCase() === p.titulo.trim().toLowerCase(),
      ),
    [allFuncoes, newFuncao],
  );

  const allFieldsValid = useMemo(() => {
    if (
      !newFuncao.nome?.trim() ||
      !newFuncao.description?.trim() ||
      !newFuncao?.code.trim() ||
      !newFuncao?.view.trim() ||
      !newFuncao?.style.trim()
    ) {
      return false;
    }
    return (
      newFuncao.variavel.every(
        (v) =>
          v.nome.trim() !== "" &&
          v.applicationId?.every((id) => id?.trim() !== "") &&
          v.keyName.every((obj) => obj.key && obj.key.trim() !== ""),
      ) &&
      newFuncao.variavel.every(
        (v) =>
          newFuncao.variavel.filter(
            (item) =>
              item.nome.trim().toLowerCase() === v.nome.trim().toLowerCase(),
          ).length === 1,
      )
    );
  }, [newFuncao]);

  // Memoize variables for PdfPreview
  const variablesMemo = useMemo(() => {
    return [
      newFuncao.variavel.map((v) => {
        return {
          nome: v.nome,
          itens: v.keyName.map((item) => item.key),
          multiple: v.multiple,
        };
      }),
    ];
  }, [newFuncao.variavel]);

  function useDebouncedValue<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState(value);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, [value, delay]);

    return debouncedValue;
  }

  const handleCodeChange = (value: string | undefined) => {
    if (codeDebounceRef.current) clearTimeout(codeDebounceRef.current);
    codeDebounceRef.current = setTimeout(() => {
      setNewFuncao((prev) => ({
        ...prev,
        code: value ?? "",
      }));
    }, 1000);
  };

  const handleViewStyleChange = (value: string | undefined) => {
    if (viewStyleDebounceRef.current)
      clearTimeout(viewStyleDebounceRef.current);
    viewStyleDebounceRef.current = setTimeout(() => {
      setNewFuncao((prev) => ({
        ...prev,
        ...(tabsValue === 1 ? { view: value ?? "" } : { style: value ?? "" }),
      }));
    }, 1000);
  };

  const debouncedView = useDebouncedValue(newFuncao.view, 1000);
  const debouncedStyle = useDebouncedValue(newFuncao.style, 1000);

  const codeMemo = useMemo(() => [newFuncao.code], [newFuncao.code]);
  const rawContentMemo = useMemo(() => [debouncedView], [debouncedView]);
  const stylesMemo = useMemo(() => {
    try {
      return [JSON.parse(debouncedStyle)];
    } catch {
      return [{}];
    }
  }, [debouncedStyle]);

  if (pageLoading) {
    return (
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <IconButton
            onClick={() => navigate("/easyReportV2/perfilRelatorios/funcao")}
          >
            <ArrowBackOutlined />
          </IconButton>
          <Typography variant="h6">Editar Função</Typography>
        </Box>
        <Paper sx={styles.paper}>
          <Box sx={styles.rowContainer}>
            <Box sx={{ flex: 1, mr: 2 }}>
              <Box sx={styles.boxTitle}>
                <TextFieldsIcon color="primary" />
                <Typography variant="subtitle2">Nome da função</Typography>
              </Box>
              <Skeleton variant="rectangular" height={50} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={styles.boxTitle}>
                <Article color="primary" />
                <Typography variant="subtitle2">Descrição</Typography>
              </Box>
              <Skeleton variant="rectangular" height={50} />
            </Box>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Box sx={styles.boxTitle}>
              <Functions color="primary" />
              <Typography>Variáveis</Typography>
            </Box>
            <Typography color={theme.palette.text.disabled} variant="subtitle1">
              Selecione os items e atibua a uma variável
            </Typography>
            <Box
              sx={{ display: "flex", gap: 2, mt: 2, mb: 2, flexWrap: "wrap" }}
            >
              {Array.from({ length: 5 }).map((_, idx) => (
                <Skeleton
                  key={idx}
                  variant="rectangular"
                  sx={{
                    flex: 1,
                    minWidth: { xs: "100%", sm: 120 },
                    height: { xs: 40, sm: 60 },
                    width: { xs: "100%", sm: 120 },
                  }}
                />
              ))}
            </Box>
          </Box>
          <Typography>Código</Typography>
          <Skeleton variant="rectangular" height={600} sx={{ mb: 2 }} />
          <Box sx={styles.buttonContainer}>
            <Skeleton
              variant="rectangular"
              width={120}
              height={36}
              sx={{ mr: 2 }}
            />
            <Skeleton variant="rectangular" width={120} height={36} />
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <IconButton
          onClick={() => navigate("/easyReportV2/perfilRelatorios/funcao")}
        >
          <ArrowBackOutlined />
        </IconButton>
        <Typography variant="h6">Editar Função</Typography>
      </Box>
      <Paper sx={styles.paper}>
        <Box sx={styles.rowContainer}>
          <Box
            sx={{ flex: 1, flexDirection: "column", display: "flex", gap: 0.5 }}
          >
            <Box sx={styles.boxTitle}>
              <TextFieldsIcon color="primary" />
              <Typography variant="subtitle2">Nome da função</Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              slotProps={{
                input: {
                  sx: {
                    color: theme.palette.text.secondary,
                  },
                },
              }}
              value={newFuncao.nome}
              onChange={(e) =>
                setNewFuncao((prev) => ({
                  ...prev,
                  nome: e.target.value,
                }))
              }
              placeholder="Nome"
              error={isNomeDuplicated}
              helperText={
                isNomeDuplicated
                  ? "O nome da função precisa ser unico"
                  : "O nome será usado no relatório para exibir o resultado "
              }
              disabled={newFuncao.isDefault}
            />
          </Box>
          <Box
            sx={{ flex: 1, flexDirection: "column", display: "flex", gap: 0.5 }}
          >
            <Box sx={styles.boxTitle}>
              <Article color="primary" />
              <Typography variant="subtitle2">Descrição</Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              slotProps={{
                input: {
                  sx: {
                    color: theme.palette.text.secondary,
                  },
                },
              }}
              value={newFuncao.description}
              onChange={(e) =>
                setNewFuncao((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Descrição"
              disabled={newFuncao.isDefault}
            />
          </Box>
        </Box>
        {/* <Box> */}

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={styles.boxTitle}>
            <Functions color="primary" />
            <Typography>Variáveis</Typography>
          </Box>
          <VariaveisForm
            variaveis={newFuncao.variavel}
            setVariaveis={(fn) =>
              setNewFuncao((prev) => ({
                ...prev,
                variavel: fn(prev).variavel,
              }))
            }
            allApplications={allApplicationsMemo}
            itemsOptions={itemsOptions}
            itemsLoading={itemsLoading}
            onApplicationChange={handleApplicationChange}
            onDelete={handleDelete}
            isLarge={isLarge}
            styles={styles}
            theme={theme}
            isFieldDuplicated={isFieldDuplicated}
            isDefault={newFuncao.isDefault}
          />
          <Button
            onClick={() => {
              const newVariable = [
                ...newFuncao.variavel,
                {
                  title: "",
                  nome: "",
                  applicationId: [""],
                  keyName: [],
                  multiple: false,
                },
              ];
              setNewFuncao((prev) => ({
                ...prev,
                variavel: newVariable,
              }));
            }}
            sx={{ width: "fit-content" }}
            disabled={newFuncao.isDefault}
          >
            <AddOutlined /> Adicionar Função
          </Button>
        </Box>
        <Box>
          <Tabs
            value={tabsValue}
            onChange={(_, value) => setTabsValue(value)}
            sx={styles.tabs}
          >
            <Tab
              label="Código"
              icon={<Code />}
              iconPosition="start"
              value={0}
              sx={{
                height: 36,
                minHeight: 36,
                "& .MuiTab-iconWrapper": { minHeight: 0 },
              }}
            />
            <Tab
              label="View"
              icon={<AssessmentOutlined />}
              iconPosition="start"
              value={1}
              sx={{
                height: 36,
                minHeight: 36,
                "& .MuiTab-iconWrapper": { minHeight: 0 },
              }}
            />
            <Tab
              label="Estilo"
              icon={<DataObject />}
              iconPosition="start"
              value={2}
              sx={{
                height: 36,
                minHeight: 36,
                "& .MuiTab-iconWrapper": { minHeight: 0 },
              }}
            />
            <Tab
              label="Ajuda"
              icon={<HelpOutline />}
              iconPosition="start"
              value={3}
              sx={{
                height: 36,
                minHeight: 36,
                "& .MuiTab-iconWrapper": { minHeight: 0 },
              }}
            />
          </Tabs>
          {tabsValue === 0 && (
            <Box sx={styles.monacoContainer}>
              <Box sx={styles.monacoCard}>
                <Box sx={styles.monacoHeader}>
                  <InputOutlined />
                  Input
                  <Button
                    variant="text"
                    color="success"
                    size="small"
                    sx={styles.headerButton}
                    onClick={handleClickRun}
                  >
                    run
                    <PlayArrowOutlined sx={{ fontSize: 16 }} />
                  </Button>
                </Box>
                <Editor
                  key="monaco-editor-code"
                  width="100%"
                  height="100%"
                  defaultLanguage="javascript"
                  defaultValue={newFuncao.code}
                  onChange={handleCodeChange}
                  theme="vs-dark"
                  options={{
                    formatOnPaste: true,
                    renderValidationDecorations: "on",
                    minimap: { enabled: true },
                    scrollBeyondLastLine: false,
                    formatOnType: true,
                    wordWrap: "on",
                    folding: true,
                    lineNumbers: "on",
                    automaticLayout: true,
                    readOnly: newFuncao.isDefault,
                  }}
                />
              </Box>
              <Box sx={styles.monacoCard}>
                <Box sx={styles.monacoHeader}>
                  <Terminal />
                  Logs
                </Box>
                <Box sx={styles.terminal}>
                  <Editor
                    key="monaco-editor-logs"
                    width="100%"
                    height="100%"
                    defaultLanguage="json"
                    value={
                      logsContent === null || logsContent === undefined
                        ? "message: Função não retornou valor"
                        : typeof logsContent === "string"
                          ? logsContent
                          : JSON.stringify(logsContent, null, 2)
                    }
                    theme="vs-dark"
                    options={{
                      formatOnPaste: true,
                      renderValidationDecorations: "on",
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      formatOnType: true,
                      wordWrap: "on",
                      folding: true,
                      lineNumbers: "on",
                      automaticLayout: true,
                      readOnly: true,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              ...styles.monacoContainer,
              display: tabsValue === 0 ? "none" : "flex",
            }}
          >
            <Box
              sx={{
                ...styles.monacoCard,
                display: tabsValue === 3 ? "none" : "flex",
              }}
            >
              <Box sx={styles.monacoHeader}>
                <InputOutlined />
                Input
              </Box>
              <Editor
                key={
                  tabsValue === 1 ? "monaco-editor-view" : "monaco-editor-style"
                }
                width="100%"
                height="100%"
                defaultLanguage={tabsValue === 1 ? "javascript" : "json"}
                defaultValue={
                  tabsValue === 1 ? newFuncao.view : newFuncao.style
                }
                onChange={handleViewStyleChange}
                theme="vs-dark"
                options={{
                  formatOnPaste: true,
                  renderValidationDecorations: "on",
                  minimap: { enabled: true },
                  scrollBeyondLastLine: false,
                  formatOnType: true,
                  wordWrap: "on",
                  folding: true,
                  lineNumbers: "on",
                  automaticLayout: true,
                  readOnly: newFuncao.isDefault,
                }}
              />
            </Box>

            {/* Help Tab Content */}
            {tabsValue === 3 && (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  p: 0,
                  background: theme.palette.background.paper,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                }}
              >
                <Box
                  sx={styles.monacoCard}
                  style={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <Box sx={styles.monacoHeader}>
                    <HelpOutline />
                    Help / Documentação
                  </Box>
                  <Box
                    sx={{ width: "100%", height: "100%", overflowY: "auto" }}
                  >
                    <HelpComponent />
                  </Box>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                ...styles.previewContainer,
                display: tabsValue === 0 || tabsValue === 3 ? "none" : "flex",
                borderRadius: 2,
              }}
            >
              <PdfPreview
                variableNames={variablesMemo}
                code={codeMemo}
                rawContent={rawContentMemo}
                styles={stylesMemo}
                setPdfError={setPdfError}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={styles.buttonContainer}>
          <Button
            sx={styles.cancelButton}
            variant="contained"
            onClick={() => navigate("/easyReportV2/perfilRelatorios/funcao")}
          >
            Cancelar
          </Button>
          <Tooltip
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: 200,
                  flexWrap: "wrap",
                  textAlign: "center",
                },
              },
            }}
            title={"Necessario preencher todos os campos"}
          >
            <span>
              <Button
                onClick={handleSave}
                disabled={
                  !allFieldsValid || isNomeDuplicated || newFuncao.isDefault
                }
                variant="contained"
              >
                Salvar
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
}

export default EditFunction;
